import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import Modal from '@components/common/Modal/Modal';
import Input from '@components/Form/Input/Input';
import Select from '@components/Form/Select/Select';
import Textarea from '@components/Form/Textarea/Textarea';
import Button from '@components/Button/Button';
import Toggle from '@components/common/Toggle';
import { ICON_OPTIONS, getModuleIcon } from './iconHelper';
import type { CreateModuleTemplatePayload } from '@/services/moduleTemplates/moduleTemplatesService';

export interface CreateModuleTemplateModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (data: CreateModuleTemplatePayload) => void | Promise<void>;
    isSubmitting?: boolean;
}

export interface ModuleTemplateFormData {
    title: string;
    slug: string;
    description: string;
    icon: string;
    route: string;
    order_no: number;
    is_cohort_specific: boolean;
    cohort_notes: string;
    is_active: boolean;
}

const slugify = (text: string) => {
    return text
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9-_]/g, '-')
        .replace(/-+/g, '-');
};

const CreateModuleTemplateModal: React.FC<CreateModuleTemplateModalProps> = ({
    isOpen,
    onClose,
    onSubmit,
    isSubmitting = false,
}) => {
    const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

    const {
        register,
        handleSubmit,
        reset,
        setValue,
        watch,
        control,
        formState: { errors },
    } = useForm<ModuleTemplateFormData>({
        defaultValues: {
            title: '',
            slug: '',
            description: '',
            icon: 'BookOpen',
            route: '/student/',
            order_no: 1,
            is_cohort_specific: false,
            cohort_notes: '',
            is_active: true,
        },
    });

    const watchedTitle = watch('title');
    const watchedIcon = watch('icon');
    const watchedIsCohortSpecific = watch('is_cohort_specific');

    useEffect(() => {
        if (!isOpen) {
            reset();
            setSlugManuallyEdited(false);
            return;
        }
    }, [isOpen, reset]);

    // Auto-populate slug from title unless manually edited
    useEffect(() => {
        if (!slugManuallyEdited && watchedTitle) {
            setValue('slug', slugify(watchedTitle), { shouldValidate: true });
        }
    }, [watchedTitle, slugManuallyEdited, setValue]);

    const handleClose = () => {
        reset();
        setSlugManuallyEdited(false);
        onClose();
    };

    const onFormSubmit = async (data: ModuleTemplateFormData) => {
        await onSubmit({
            title: data.title.trim(),
            slug: data.slug.trim(),
            description: data.description?.trim() || undefined,
            icon: data.icon || 'BookOpen',
            route: data.route.trim(),
            order_no: Number(data.order_no) || 1,
            is_cohort_specific: Boolean(data.is_cohort_specific),
            cohort_notes: data.cohort_notes?.trim() || undefined,
            is_active: Boolean(data.is_active),
        });
        handleClose();
    };

    const SelectedIcon = getModuleIcon(watchedIcon);

    return (
        <Modal isOpen={isOpen} onClose={handleClose} title="Create Module Template">
            <form
                id="create-module-template-form"
                onSubmit={handleSubmit(onFormSubmit)}
                noValidate
                className="overflow-y-auto max-h-[75vh] p-4 2xl:p-5 flex flex-col gap-4 text-left"
            >
                {/* Title & Slug */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                        label="Module Title"
                        placeholder="e.g. Skill Profile"
                        required
                        registration={register('title', {
                            required: 'Module title is required',
                            validate: val => val.trim().length > 0 || 'Title cannot be empty',
                        })}
                        error={errors.title?.message}
                    />

                    <div>
                        <Input
                            label="Module Slug (Unique identifier)"
                            placeholder="e.g. skill-profile"
                            required
                            registration={register('slug', {
                                required: 'Slug is required',
                                onChange: () => setSlugManuallyEdited(true),
                                validate: val =>
                                    /^[a-z0-9-_]+$/.test(val.trim()) ||
                                    'Slug can only contain lowercase letters, numbers, hyphens, and underscores',
                            })}
                            error={errors.slug?.message}
                        />
                        <span className="text-[11px] text-gray-500 mt-1 block">
                            Used in backend matching and cohort assignment
                        </span>
                    </div>
                </div>

                {/* Route & Icon */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                        label="Student Portal Route"
                        placeholder="e.g. /student/skill-profile"
                        required
                        registration={register('route', {
                            required: 'Route is required',
                            validate: val =>
                                val.trim().startsWith('/') || 'Route must start with / (e.g. /student/...)',
                        })}
                        error={errors.route?.message}
                    />

                    <div>
                        <label className="text-[12px] 2xl:text-[14px] font-medium text-[var(--secondary-color)] block mb-1">
                            Menu Icon
                        </label>
                        <div className="flex items-center gap-2">
                            <div className="w-10 h-10 rounded-lg border border-gray-200 bg-blue-50/50 flex items-center justify-center text-primary shrink-0">
                                <SelectedIcon size={20} />
                            </div>
                            <div className="flex-1">
                                <Controller
                                    name="icon"
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            placeholder="Select Icon"
                                            options={ICON_OPTIONS}
                                            value={field.value}
                                            onChange={field.onChange}
                                        />
                                    )}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Order No & Status */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
                    <Input
                        label="Display Order (Sequence in sidebar)"
                        type="number"
                        placeholder="1"
                        required
                        registration={register('order_no', {
                            valueAsNumber: true,
                            required: 'Order number is required',
                            min: { value: 1, message: 'Minimum order is 1' },
                        })}
                        error={errors.order_no?.message}
                    />

                    <div className="flex flex-col gap-1.5 pt-2">
                        <label className="text-[12px] 2xl:text-[14px] font-medium text-[var(--secondary-color)]">
                            Template Status
                        </label>
                        <div className="flex items-center justify-between px-4 py-2.5 rounded-lg border border-gray-200 bg-[var(--primary-canvas-color)]">
                            <span className="text-xs font-semibold text-gray-700">
                                {watch('is_active') ? 'Active (Available)' : 'Inactive (Hidden)'}
                            </span>
                            <Controller
                                name="is_active"
                                control={control}
                                render={({ field }) => (
                                    <Toggle active={field.value} onChange={field.onChange} />
                                )}
                            />
                        </div>
                    </div>
                </div>

                {/* Cohort Specific Toggle */}
                <div className="p-3.5 rounded-xl border border-dashed border-amber-200 bg-amber-50/40 flex flex-col gap-3">
                    <div className="flex items-center justify-between">
                        <div>
                            <span className="text-xs font-bold text-gray-900 block">
                                Cohort Specific Module
                            </span>
                            <span className="text-[11px] text-gray-500">
                                Enable if this module is conditionally enabled for specific cohorts (e.g. Flagship / Cohort 1).
                            </span>
                        </div>
                        <Controller
                            name="is_cohort_specific"
                            control={control}
                            render={({ field }) => (
                                <Toggle active={field.value} onChange={field.onChange} />
                            )}
                        />
                    </div>

                    {watchedIsCohortSpecific && (
                        <Controller
                            name="cohort_notes"
                            control={control}
                            render={({ field }) => (
                                <Textarea
                                    label="Cohort Specific Context / Notes (Optional)"
                                    placeholder="e.g. Only enabled for Flagship Cohort 1 participants with verified projects"
                                    rows={2}
                                    {...field}
                                />
                            )}
                        />
                    )}
                </div>

                {/* Description */}
                <Controller
                    name="description"
                    control={control}
                    render={({ field }) => (
                        <Textarea
                            label="Module Description (Optional)"
                            placeholder="Briefly describe what students access or do in this module"
                            rows={2}
                            {...field}
                        />
                    )}
                />
            </form>

            <div className="flex items-center justify-between border-t border-[var(--neutral-color)] px-4 2xl:px-5 py-2 2xl:py-3">
                <Button type="button" variant="dark" onClick={handleClose} disabled={isSubmitting}>
                    Cancel
                </Button>

                <Button
                    type="submit"
                    variant="primary"
                    form="create-module-template-form"
                    disabled={isSubmitting}
                >
                    {isSubmitting ? 'Creating...' : 'Create Template'}
                </Button>
            </div>
        </Modal>
    );
};

export default CreateModuleTemplateModal;
